import * as CDM from 'trimble-connect-sdk';
import type { FolderEntry } from './types';

export interface ApiRequestConfig {
  readonly headers?: Record<string, string>;
  readonly cancelToken?: AbortSignal;
}

export const AttachmentPickerApiService = {
  /**
   * List folder entries using the same pattern as the existing tcweb implementation
   */
  listFolderEntries: async function (
    origin: string,
    folderId: string,
    config?: ApiRequestConfig,
    sort?: string
  ): Promise<{ data: FolderEntry[]; headers: Record<string, string> }> {
    let url = `${AttachmentPickerApiService.getOriginUrl(
      origin
    )}/folders/${folderId}/items?tokenThumburl=false`;
    if (sort) {
      url += `&sort=${sort}`;
    }

    const headers: Record<string, string> = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    // Add authorization if available
    if (CDM.TCPSClient.config.credentials?.token) {
      headers['Authorization'] =
        `Bearer ${CDM.TCPSClient.config.credentials.token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: config?.cancelToken,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        const statusCode = response.status;
        const errorMessage = `HTTP error! status: ${String(statusCode)}, message: ${errorText}`;
        throw new Error(errorMessage);
      }

      const data = (await response.json()) as unknown[];

      // Extract headers for pagination info
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      // Transform the data to match our interface
      const transformedData: FolderEntry[] = data.map(
        (item: unknown): FolderEntry => {
          const typedItem = item as Record<string, unknown>;
          const baseItem = {
            id: String(typedItem['id'] || ''),
            name: String(typedItem['name'] || ''),
            thumbnailUrl: typedItem['thumbnailUrl']
              ? [String(typedItem['thumbnailUrl'])]
              : undefined,
            parentId: String(typedItem['parentId'] || ''),
            createdOn: typedItem['createdOn']
              ? String(typedItem['createdOn'])
              : undefined,
            modifiedOn: typedItem['modifiedOn']
              ? String(typedItem['modifiedOn'])
              : undefined,
          };

          if (typedItem['type'] === 'FILE') {
            return {
              ...baseItem,
              type: 'FILE' as const,
              versionId: typedItem['versionId']
                ? String(typedItem['versionId'])
                : undefined,
              size:
                typeof typedItem['size'] === 'number'
                  ? typedItem['size']
                  : undefined,
            };
          } else {
            return {
              ...baseItem,
              type: 'FOLDER' as const,
            };
          }
        }
      );

      return {
        data: transformedData,
        headers: responseHeaders,
      };
    } catch (error) {
      console.error('listFolderEntries fetch error:', error);
      if (
        error instanceof TypeError &&
        error.message.includes('Failed to fetch')
      ) {
        throw new Error(
          `Network error: Unable to reach ${url}. Check CORS settings and network connectivity.`
        );
      }
      throw error;
    }
  },

  /**
   * Get folder information
   */
  getFolder: async function (
    project: CDM.Project,
    folderId: string
  ): Promise<{ id: string; name: string; parentId?: string }> {
    const response = await CDM.TCPSClient.getFolder(project, folderId);
    const folderData = response.data;

    return {
      id: folderData.id,
      name: folderData.name,
      parentId: folderData.parentId,
    };
  },

  /**
   * Get origin URL - similar to the existing tcweb implementation
   */
  getOriginUrl: function (origin: string): string {
    // The origin parameter should be in format like "//app.connect.trimble.com"
    // We need to add the protocol and API suffix: "https://app.connect.trimble.com/tc/api/2.0"
    const apiPrefix = 'https:';
    const publicApiSuffix = '/tc/api/2.0';

    return `${apiPrefix}${origin}${publicApiSuffix}`;
  },

  /**
   * Parse content range header to get total count
   */
  parseContentRange: function (contentRange: string): {
    total: number;
    from: number;
    to: number;
  } {
    // Format: "items 0-24/100" or "0-24/100"
    const match = contentRange.match(/(\d+)-(\d+)\/(\d+)/);
    if (match) {
      const radix = 10;
      const fromIndex = 1;
      const toIndex = 2;
      const totalIndex = 3;
      return {
        from: parseInt(match[fromIndex], radix),
        to: parseInt(match[toIndex], radix),
        total: parseInt(match[totalIndex], radix),
      };
    }
    return { from: 0, to: 0, total: 0 };
  },

  /**
   * Create pagination headers
   */
  createPaginationHeaders: function (
    offset: number,
    limit: number
  ): Record<string, string> {
    const endRange = offset + limit - 1;
    const rangeValue = `items=${String(offset)}-${String(endRange)}`;
    return {
      range: rangeValue,
    };
  },
};
